import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Globe, 
  MapPin, 
  Zap, 
  Shield, 
  Search, 
  BarChart3, 
  Clock, 
  Users,
  Smartphone,
  Monitor
} from 'lucide-react';
import Layout from './Layout';
import PageHero from './PageHero';

const FeaturesPage = () => {
  const features = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Global Coverage",
      description: "Check Google results from more than 230 countries and territories, in any of about 150 interface languages.",
      color: "blue"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Precise Location Targeting",
      description: "Add a city, an address or a \"lat, lng\" pair. The tool finds its coordinates and passes them to Google, so the map pack and local results match that spot.",
      color: "green"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "No VPN or proxy",
      description: "Skip VPN subscriptions and proxy setups. The location is sent to Google via URL parameters, so results load like any normal search.",
      color: "yellow"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "100% Free & Secure",
      description: "No account, no credit card, no daily quota. Your keywords are not stored on our servers: your browser talks to Google directly.",
      color: "purple"
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: "Built-in Google autocomplete",
      description: "Google Suggest completes your query as you type, so you check the terms people actually search for.",
      color: "indigo"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "The real results page",
      description: "You see Google's own page, with ads, map pack and SERP features, and read positions off it yourself. The tool does not build a rank table or keep history.",
      color: "red"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Live, never cached",
      description: "The page loads from google.com when you click Search, not from a stored copy. Rankings move during the day, so two checks can differ.",
      color: "teal"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multi-language Support",
      description: "Check results in any of Google's host languages — the language selector covers the full list, not just a handful.",
      color: "pink"
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile Responsive",
      description: "Fully optimized for mobile devices. Check SERPs on your phone, tablet, or desktop seamlessly.",
      color: "orange"
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: "No Software Installation",
      description: "Web-based tool that works in any browser. No downloads, installations, or technical setup required.",
      color: "gray"
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: "bg-blue-100 text-blue-600",
      green: "bg-green-100 text-green-600",
      yellow: "bg-yellow-100 text-yellow-600",
      purple: "bg-purple-100 text-purple-600",
      indigo: "bg-indigo-100 text-indigo-600",
      red: "bg-red-100 text-red-600",
      teal: "bg-teal-100 text-teal-600",
      pink: "bg-pink-100 text-pink-600",
      orange: "bg-orange-100 text-orange-600",
      gray: "bg-gray-100 text-gray-600"
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <Layout breadcrumbItems={[
      { label: 'Features', href: '/features/' }
    ]}>
      <Helmet defer={false}>
        <title>Features - Free Google SERP Checker Tool</title>
        <meta name="description" content="Discover the powerful features of our free Google SERP checker tool. Global coverage, precise location targeting, real-time results, no VPN required." />
        <meta name="keywords" content="serp checker features, google serp checker features, serp checker tool features, serp analysis features, serp checker capabilities, free serp checker features, local serp checker features, international serp checker" />
        <link rel="canonical" href="https://freeserpchecker.org/features/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://freeserpchecker.org/features/" />
        <meta property="og:title" content="Features - Free Google SERP Checker Tool" />
        <meta property="og:description" content="Discover the powerful features of our free Google SERP checker tool. Global coverage, precise location targeting, real-time results, no VPN required." />
        <meta property="og:image" content="https://freeserpchecker.org/og-image.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://freeserpchecker.org/features/" />
        <meta property="twitter:title" content="Features - Free Google SERP Checker Tool" />
        <meta property="twitter:description" content="Discover the powerful features of our free Google SERP checker tool. Global coverage, precise location targeting, real-time results, no VPN required." />
        <meta property="twitter:image" content="https://freeserpchecker.org/og-image.png" />
      </Helmet>

      <section id="features" className="section-padding bg-gray-50">
        <div className="container-max">
          <PageHero
            title="Features for SERP analysis"
            subtitle="Everything you need to see Google's search results from any location worldwide — built for SEO professionals, marketers and businesses."
          />

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="card p-6 hover-lift"
              >
                <div className={`w-16 h-16 ${getColorClasses(feature.color)} rounded-xl flex items-center justify-center mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Start Analyzing SERPs?
              </h3>
              <p className="text-gray-600 mb-6">
                Try our free Google SERP checker tool now and see how search results vary by location.
              </p>
              <a 
                href="/"
                className="btn-primary text-lg px-8 py-3 inline-block"
              >
                Start Checking SERPs Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FeaturesPage;
